/**
 * Reusable CSV Import Dialog.
 * Used by Hardware Presets, Mfr Warranty Defaults, Battery Presets (and
 * any future bulk-upserted lookups).
 *
 * Props:
 *  - open / onOpenChange   : dialog state
 *  - title                 : header text (e.g. "Import Battery Presets from CSV")
 *  - description           : sub-header copy
 *  - templateUrl           : GET endpoint returning a CSV template
 *  - importUrl             : POST endpoint accepting multipart `file` field
 *  - templateFilename      : suggested filename when downloading the template
 *  - onSuccess             : callback after a successful import (e.g. reload list)
 *  - colorClass            : tailwind colour theme for the primary button (default sky)
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Upload, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const CsvImportDialog = ({
  open,
  onOpenChange,
  title,
  description,
  templateUrl,
  importUrl,
  templateFilename = 'template.csv',
  onSuccess,
  colorClass = 'bg-sky-600 hover:bg-sky-700',
  iconColor = 'text-sky-600',
}) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setImporting(false);
    setResult(null);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(templateUrl, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = templateFilename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Template download failed');
    }
  };

  const doImport = async () => {
    if (!file) { toast.error('Please pick a .csv file first'); return; }
    setImporting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(importUrl, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast.success(`Imported: ${data.created} created · ${data.updated} updated${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ''}`);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const inputId = `csv-input-${importUrl.replace(/\W+/g, '-')}`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className={`w-5 h-5 ${iconColor}`} />
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
              <p className="font-semibold text-slate-700 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Step 1 — Get the template
              </p>
              <p className="text-slate-600">
                Download the CSV template with the exact column headers + example rows.
                Open in Excel / Google Sheets, fill it in, save as CSV.
              </p>
              <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Download template
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Upload className="w-3.5 h-3.5" /> Step 2 — Upload your CSV
              </p>
              <label
                htmlFor={inputId}
                className="block border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/50 transition-colors"
                data-testid="csv-dropzone"
              >
                {file ? (
                  <div className="space-y-1">
                    <FileText className={`w-8 h-8 ${iconColor} mx-auto`} />
                    <p className="text-sm font-medium text-slate-800">{file.name}</p>
                    <p className="text-[11px] text-slate-500">{(file.size / 1024).toFixed(1)} KB · click to choose another</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                    <p className="text-sm text-slate-600">Click to choose a .csv file</p>
                    <p className="text-[11px] text-slate-400">or drag and drop</p>
                  </div>
                )}
              </label>
              <input
                id={inputId}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="csv-file-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="import-result">
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="font-semibold text-emerald-900">Import complete</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded bg-white p-2 border border-emerald-200">
                  <p className="text-2xl font-bold text-emerald-600">{result.created}</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Created</p>
                </div>
                <div className="rounded bg-white p-2 border border-sky-200">
                  <p className="text-2xl font-bold text-sky-600">{result.updated}</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Updated</p>
                </div>
                <div className="rounded bg-white p-2 border border-amber-200">
                  <p className="text-2xl font-bold text-amber-600">{result.skipped?.length || 0}</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Skipped</p>
                </div>
              </div>
            </div>
            {result.skipped?.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 max-h-40 overflow-y-auto">
                <p className="text-xs font-semibold text-amber-900 mb-1">Skipped rows:</p>
                <ul className="text-[11px] text-amber-800 space-y-0.5">
                  {result.skipped.map((s, i) => (
                    <li key={i}>Row {s.row}: {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)}>Cancel</Button>
              <Button
                onClick={doImport}
                disabled={importing || !file}
                className={colorClass}
                data-testid="confirm-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Import
              </Button>
            </>
          ) : (
            <Button onClick={() => handleClose(false)} className={colorClass} data-testid="close-import-btn">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CsvImportDialog;
